/**
 * 제안 카드 — 설계 v7 §7.
 *
 * 네 신호를 한 화면에 놓는다. **확정을 만들지 않는다** — 행을 눌러도 참고 강조만 하고
 * 상품 사실·PROVE 입력·재계산을 건드리지 않는다. 배지가 축약형일 때도 "법적 확정 아님"은
 * 지우지 않는다. 그 한 줄이 이 카드의 전부다.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { onAuthChange } from './authState';
import { tariffNameMatches } from './tariffNameMatch';
import {
  allConditionsTrue,
  clearSessionKeys,
  fetchProposal,
  isTrue,
  recordAdoption,
} from './proposalClient';

const CONDITION_LABELS = {
  lines_ge2: '같은 기재 2건 이상',
  decls_ge2: '독립 신고 2건 이상',
  single_hsk: '세번이 하나',
  active: '현행 세번',
  not_amended_before: '변경고시 종전 세번 아님',
};

/** 색은 파랑·회색만 쓴다. 경고색은 이 제품에서 쓰지 않는다. */
const STYLE = {
  card: { border: '1px solid #dbe1e8', borderRadius: 4, padding: 16, background: '#fff' },
  badgeMatch: { background: '#e6edf6', color: '#1d4e89', border: '1px solid #a9c2e0' },
  badgeProposal: { background: '#eceff3', color: '#5c6775', border: '1px solid #dbe1e8' },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: 3, fontSize: 12 },
  muted: { color: '#5c6775', fontSize: 12 },
  row: { display: 'flex', gap: 12, alignItems: 'center', padding: '6px 0' },
};

function conditionMark(condition) {
  if (!condition) return '—';
  if (condition.value === 'TRUE') return '✓';
  if (condition.value === 'FALSE') return '—';
  return '확인 불가';
}

/**
 * 서버 사유 코드를 사람이 읽는 문구로 바꾼다.
 *
 * 코드를 그대로 보이면 화면에 `(blank_normalized_query)` 같은 것이 뜬다(코드 리뷰 v3).
 * 모르는 코드는 괄호 안에 그대로 둔다 — 지어내는 것보다 낫다.
 */
export function reasonText(reason) {
  const MAP = {
    blank_normalized_query: '기재에서 비교할 글자가 남지 않았습니다',
    spec_norm_incomplete: '이 사무소의 기재 정규화가 아직 끝나지 않았습니다',
    query_failed: '조회에 실패했습니다',
    no_group: '사무소를 알 수 없습니다',
    not_authenticated: '로그인이 필요합니다',
  };
  if (!reason) return '사유 미상';
  return MAP[reason] || `사유 코드 ${reason}`;
}

/** 서버가 쉼표로 이어 보내는 경고를 낱개로 가른다. 같은 코드가 두 번 오면 한 번만 그린다. */
export function warnings(history) {
  return [...new Set(
    String((history && history.history_warning) || '')
      .split(',')
      .map((warning) => warning.trim())
      .filter(Boolean),
  )];
}

/** 경고 문구. 모르는 코드는 그대로 보인다 — 지어내는 것보다 낫다. */
export function warningText(warning) {
  const MAP = {
    same_text_different_codes: '같은 기재에 다른 세번이 관측됩니다.',
    target_mismatch: '조회한 세번과 조건이 본 세번이 달라 일부 조건을 확인하지 못했습니다.',
  };
  return MAP[warning] || `경고 코드 ${warning}`;
}

export function HistoryDetail({ history }) {
  if (!history) return null;
  if (history.status !== 'ok') {
    return (
      <div style={STYLE.muted}>
        과거 신고 확인 불가 — {reasonText(history.reason)}
      </div>
    );
  }
  const population = history.population || {};
  const retrieved = population.retrieved || {};
  const invalid = retrieved.invalid || {};
  const invalidTotal = Object.values(invalid).reduce((sum, n) => sum + (n || 0), 0);
  return (
    <div>
      <div style={STYLE.muted}>
        회수 {retrieved.retrieved_rows || 0}행
        {retrieved.truncated ? ` (${retrieved.exact_total_at_least}행 이상, 잘림)` : ''}
        {' · '}기간 내 유효 {retrieved.in_window_valid || 0}
        {invalidTotal > 0 ? ` · 무효 ${invalidTotal}` : ''}
      </div>
      {retrieved.out_of_window_valid > 0 && (
        <div style={STYLE.muted}>기간 밖 이력에 다른 세번 있음</div>
      )}
      <div style={STYLE.muted}>정정 이력 미반영</div>
      {(history.by_code || []).map((entry) => (
        <div key={entry.hs_code} style={STYLE.row}>
          <strong>{entry.hs_code}</strong>
          <span style={STYLE.muted}>
            {entry.lines}행 · 독립 신고 {entry.declarations}건 · {entry.first}~{entry.last}
          </span>
        </div>
      ))}
      {history.conditions && (
        <table style={{ fontSize: 12, marginTop: 8 }}>
          <tbody>
            {Object.keys(CONDITION_LABELS).map((key) => (
              <tr key={key}>
                <td style={{ paddingRight: 12, color: '#5c6775' }}>{CONDITION_LABELS[key]}</td>
                <td>{conditionMark(history.conditions[key])}</td>
                <td style={{ paddingLeft: 8, color: '#838d9a' }}>
                  {history.conditions[key] && history.conditions[key].reason}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {/* 경고는 쉼표로 이어 올 수 있다. 같다고 비교하면 이어진 경고가 통째로 사라진다
          (`same_text_different_codes,target_mismatch`, 코드 리뷰 v4). */}
      {warnings(history).map((warning) => (
        <div key={warning} style={STYLE.muted}>{warningText(warning)}</div>
      ))}
    </div>
  );
}

/** 일치 판정은 한 곳에서만 한다 — 배지와 문구가 갈라지면 둘 중 하나가 거짓말을 한다. */
export function isHistoryMatched(history) {
  return !!history && history.status === 'ok' && history.history_match === true
    && allConditionsTrue(history.conditions);
}

export function ProposalBadge({ history }) {
  const matched = isHistoryMatched(history);
  if (matched) {
    const declarations = (history.by_code || []).reduce(
      (max, entry) => Math.max(max, entry.declarations || 0), 0);
    return (
      <span style={{ ...STYLE.badge, ...STYLE.badgeMatch }}>
        과거 신고 일치 · 36개월 · 독립 신고 {declarations}건 · 법적 확정 아님
      </span>
    );
  }
  return <span style={{ ...STYLE.badge, ...STYLE.badgeProposal }}>제안 · 확정 아님</span>;
}

/**
 * 카드가 물어도 되는 시점 — 관세율표 조회가 끝났을 때.
 *
 * 상품 설명은 셸의 조회 응답(`tariffData.productDetails`)에 실려 오므로, 조회가 진행 중인데
 * 먼저 물으면 설명 없이 한 번, 도착하면 또 한 번 묻는다. 셸 조회가 없는 경로(폴백 keyword
 * 조회)는 그 결과가 오면 된다 — 폴백은 실패해도 빈 결과로 반드시 끝난다.
 */
export function isProposalReady({ q, tariffData, lookupInFlight, tariff }) {
  if (!q) return false;
  if (tariffData && tariffData.q === q) return true;      // 셸 응답이 이 검색어로 도착
  if (lookupInFlight === q) return false;                  // 셸이 이 검색어를 조회 중
  return Boolean(tariff);                                   // 폴백 결과가 있으면 된다
}

/**
 * 사무소 과거 신고가 다섯 조건을 전부 통과했을 때만 보이는 한 줄. 검색 결과 화면에서는 카드를 숨기고
 * (신호는 지도·표 마디 배지로 이미 보인다) 이 줄만 남긴다 — 2026-09-14 사용자: "카드는 숨기고 일치할 때만 한 줄로".
 * 세번을 누르면 채택 기록(이 기능의 유일한 지표)을 남기고 참고 강조만 한다. 확정을 만들지 않는다.
 */
export function HistoryMatchLine({ proposal, onHighlight }) {
  const history = proposal && proposal.signals ? proposal.signals.history : null;
  if (!isHistoryMatched(history)) return null;
  const codes = history.by_code || [];
  const declarations = codes.reduce((max, entry) => Math.max(max, entry.declarations || 0), 0);
  return (
    <div data-testid="history-match-line" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12, padding: '6px 10px', border: '1px solid #a9c2e0', borderRadius: 4, background: '#e6edf6', color: '#1d4e89' }}>
      <strong>과거 신고 일치</strong>
      <span>최근 36개월 우리 사무소의 같은 기재 · 독립 신고 {declarations}건 · 법적 확정 아님</span>
      {codes.map((entry) => (
        <button
          type="button"
          key={`hm-${entry.hs_code}`}
          onClick={() => { recordAdoption(proposal.request_hash, 'history'); if (onHighlight) onHighlight(entry.hs_code); }}
          title={`${entry.lines}행 · 독립 신고 ${entry.declarations}건 · ${entry.first}~${entry.last}`}
          style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, border: '1px solid #a9c2e0', borderRadius: 3, background: '#fff', color: '#1d4e89', padding: '1px 6px', cursor: 'pointer' }}
        >
          {entry.hs_code}
        </button>
      ))}
    </div>
  );
}

export default function ProposalCard({ query, productDetails, tariffItems, onHighlight, onLoaded, ready = true, visible = true }) {
  const [state, setState] = useState({ status: 'idle' });
  const abortRef = useRef(null);
  // onLoaded 는 부모가 매 렌더마다 새 함수로 주기 쉽다(인라인 화살표). 그것을 run 의 의존성에
  // 두면 "응답 → 부모 setState → 새 onLoaded → run 재생성 → 다시 묻기" 되풀이가 사용자 분당 상한
  // (10회)에 걸릴 때까지 돌아, 뒤이은 더 묻기가 429 로 막혔다(2026-09-14 운영 로그: 로드 3초에 20회).
  // 최신 콜백은 ref 로 들고, 다시 묻는 조건은 검색어·상품 설명뿐이다.
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  const run = useCallback(async () => {
    if (!query) return;
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setState({ status: 'loading' });
    const result = await fetchProposal({
      query,
      productDetails,
      signal: controller.signal,
    });
    const onLoaded = onLoadedRef.current;
    if (result.status === 'ok') { setState({ status: 'ok', data: result.data }); if (onLoaded) onLoaded(result.data); }
    else if (result.status === 'stale') { setState({ status: 'idle' }); if (onLoaded) onLoaded(null); }
    else if (result.status === 'error') { setState({ status: 'error' }); if (onLoaded) onLoaded(null); }
  }, [query, productDetails]);

  // ready 전에는 묻지 않는다 — 셸 조회가 끝나 상품 설명이 함께 오면 그때 한 번.
  useEffect(() => {
    if (!ready) { setState({ status: 'loading' }); return undefined; }
    run();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [run, ready]);

  // 계정·사무소가 바뀌면 진행 중 요청을 끊고 사설 상태를 비운다. 남겨 두면 이전 계정의
  // 이력이 화면에 남는다. **그리고 곧바로 다시 묻는다.**
  //
  // 비우기만 하면 자동 로그인에서 카드가 죽는다 — 페이지를 열 때마다 토큰을 갱신하고
  // 그때마다 세대가 오르는데, 카드는 idle 로 돌아간 뒤 다시 묻지 않아 검색어가 있는데도
  // "검색어를 입력하면…" 에 멈춘다. 새로고침·딥링크·PWA 갱신마다 그랬다(코드 리뷰 v10).
  useEffect(() => onAuthChange(() => {
    if (abortRef.current) abortRef.current.abort();
    clearSessionKeys();
    setState({ status: 'idle' });
    if (ready) run();           // 새 세대로 다시 묻는다. 계정이 바뀌었어도 맞는 동작이다.
    // ready 전이면 조회가 끝날 때 run 이 새 세대로 묻는다 — 지금 묻고 또 묻지 않는다.
  }), [run, ready]);

  // 숨김 모드 — 요청·onLoaded 는 그대로(지도·표 배지·후보 호 라우팅의 재료), 그리기만 하지 않는다.
  if (!visible) return null;

  if (state.status !== 'ok') {
    return (
      <div style={STYLE.card} data-testid="proposal-card">
        <div style={STYLE.muted}>
          {state.status === 'loading' ? '신호를 모으는 중입니다.'
            : state.status === 'error' ? '신호를 불러오지 못했습니다.'
            : '검색어를 입력하면 제안을 보여 줍니다.'}
        </div>
      </div>
    );
  }

  const signals = state.data.signals || {};
  const history = signals.history;
  const llm = signals.llm;
  const requestHash = state.data.request_hash;
  // ④ 관세율표 명칭 일치는 기존 함수를 그대로 쓴다(설계 v7 §4-6). 규칙은 건드리지 않는다.
  const tariffMatches = tariffNameMatches(query, tariffItems || []);

  const matched = isHistoryMatched(history);
  const precedentGroups = signals.precedent && signals.precedent.status === 'ok'
    && signals.precedent.hints ? (signals.precedent.hints.by_hsk || []) : [];

  // 어느 신호를 가져갔는지 남긴다. 이것이 이 기능의 유일한 성능 지표다(구현지시 §5).
  const take = (signal, hsk) => {
    recordAdoption(requestHash, signal);
    if (onHighlight) onHighlight(hsk);
  };
  return (
    <div style={STYLE.card} data-testid="proposal-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <strong>제안</strong>
        <ProposalBadge history={history} />
      </div>
      {matched ? (
        // 일치일 때만 일치를 주장한다. 비로그인·불일치·확인 불가에도 띄우면 배지와 모순된다.
        <div style={{ ...STYLE.muted, marginTop: 6 }}>
          최근 36개월 자기 사무소의 동일 정규화 기재에서 관측된 신고 세번이 일치합니다.
          동일 물품 식별·법적 적합성 보장이 아닙니다.
        </div>
      ) : (
        <div style={{ ...STYLE.muted, marginTop: 6 }}>
          아래는 참고 신호이며 확정이 아닙니다. 최종 판단은 결정트리가 합니다.
        </div>
      )}
      {llm && llm.status === 'ok' && llm.candidates.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={STYLE.muted}>제안 후보 · 확정 아님</div>
          {llm.candidates.map((candidate) => (
            <button
              type="button"
              key={`llm-${candidate.code}`}
              onClick={() => take('llm', candidate.code)}
              title={candidate.why}
              style={{ marginRight: 6, marginTop: 4 }}
            >
              {candidate.code} ({candidate.level})
            </button>
          ))}
        </div>
      )}
      {llm && llm.status !== 'ok' && (
        <div style={STYLE.muted}>제안 후보 없음 ({llm.reason || llm.status})</div>
      )}
      {tariffMatches.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={STYLE.muted}>관세율표 품명 일치 · 확정 아님</div>
          {tariffMatches.map((match) => (
            <button
              type="button"
              key={`tariff-${match.hsk}`}
              onClick={() => take('tariff', match.hsk)}
              // 괄호 안의 법정 조건을 뺀 이름이 맞은 경우다. 표시를 생략하면 제4015호
              // 같은 조건부 후보가 무조건 일치처럼 읽힌다(코드 리뷰 v2 SHOULD).
              title={match.qualified
                ? `${match.ko || match.en} — 괄호 안 조건을 뺀 이름이 일치했다. 부모 호 조건은 확인해야 한다.`
                : (match.ko || match.en)}
              style={{ marginRight: 6, marginTop: 4 }}
            >
              {match.hsk} 관세율표{match.qualified ? ' · 조건부' : ''}
            </button>
          ))}
        </div>
      )}
      {precedentGroups.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={STYLE.muted}>국내 결정례 · 근거이며 확정 아님</div>
          {precedentGroups.slice(0, 5).map((group) => (
            <button
              type="button"
              key={`prec-${group.hsk}`}
              onClick={() => take('precedent', group.hsk)}
              title={group.label || ''}
              style={{ marginRight: 6, marginTop: 4 }}
            >
              {group.hsk} 결정례 {group.count}건
            </button>
          ))}
        </div>
      )}
      {signals.precedent && signals.precedent.status !== 'ok' && (
        <div style={STYLE.muted}>결정례 힌트 없음 ({signals.precedent.reason})</div>
      )}
      {history ? <HistoryDetail history={history} />
        : <div style={STYLE.muted}>로그인하면 자기 사무소 과거 신고를 함께 봅니다.</div>}
      {(history && history.by_code ? history.by_code : []).map((entry) => (
        <button
          type="button"
          key={`pick-${entry.hs_code}`}
          onClick={() => take('history', entry.hs_code)}
          style={{ marginRight: 6, marginTop: 6 }}
        >
          {entry.hs_code} 참고
        </button>
      ))}
    </div>
  );
}

export { isTrue };
